// food-management-app/src/GeneralInstructionsManager.js
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Paper, CircularProgress, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, IconButton,
  Snackbar, Alert, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Edit, Delete, Add, DragHandle } from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import EnhancedRichTextEditor from './components/EnhancedRichTextEditor';
import api from './api';
import { useAuth } from './context/AuthContext';

const DEFAULT_LANGUAGE = 'english';
const SUPPORTED_LANGUAGES = ['english', 'german', 'french', 'spanish', 'italian', 'portuguese', 'russian', 'chinese', 'japanese', 'korean', 'arabic', 'hindi', 'dutch'];

// Draggable table row component
const DraggableRow = ({ id, index, moveRow, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'row',
    item: { id, index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'row',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveRow(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <TableRow
      ref={node => drag(drop(node))}
      style={{ 
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
      }}
    >
      {children}
    </TableRow>
  );
};

const GeneralInstructionsManager = () => {
  const { isAdmin } = useAuth();
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [tempInstructions, setTempInstructions] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/general-instructions');
      const instructionsData = response.data.data || [];
      setInstructions(instructionsData);
      setTempInstructions(instructionsData);
    } catch (error) {
      console.error('Error fetching general instructions:', error);
      showSnackbar('Failed to fetch general instructions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReorderToggle = () => {
    if (isReorderMode) {
      // Cancel reordering
      setTempInstructions(instructions);
      setIsReorderMode(false);
    } else {
      // Start reordering
      setIsReorderMode(true);
    }
  };

  const moveRow = (fromIndex, toIndex) => {
    const updatedInstructions = [...tempInstructions];
    const [movedItem] = updatedInstructions.splice(fromIndex, 1);
    updatedInstructions.splice(toIndex, 0, movedItem);
    setTempInstructions(updatedInstructions);
  };

  const saveReordering = async () => {
    try {
      setLoading(true);
      
      // Prepare the order data
      const orderData = tempInstructions.map((instruction, index) => ({
        id: instruction.id,
        sortOrder: index
      }));
      
      await api.post('/general-instructions/reorder', { order: orderData });
      
      // Update the main instructions state and exit reorder mode
      setInstructions(tempInstructions);
      setIsReorderMode(false);
      showSnackbar('Instructions reordered successfully', 'success');
    } catch (error) {
      console.error('Error saving instruction order:', error);
      showSnackbar('Failed to save instruction order', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({
      title: '',
      description: '',
      isActive: true
    });
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (instruction) => {
    setSelectedInstruction(instruction);
    // Get localized content for editing
    const localizedTitle = instruction.languageSpec?.[currentLanguage]?.name || instruction.title || '';
    const localizedDescription = instruction.languageSpec?.[currentLanguage]?.description || instruction.description || '';
    
    setFormData({
      title: localizedTitle,
      description: localizedDescription,
      isActive: instruction.isActive
    });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (instruction) => {
    setSelectedInstruction(instruction);
    setOpenDeleteDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'isActive' ? checked : value
    });
  };

  const handleDescriptionChange = (description) => {
    setFormData({
      ...formData,
      description
    });
  };

  const handleAddInstruction = async () => {
    try {
      setLoading(true);
      
      if (!formData.title || !formData.description) {
        showSnackbar('Title and description are required', 'error');
        return;
      }
      
      await api.post('/general-instructions', formData);
      
      setOpenAddDialog(false);
      showSnackbar('General instruction created successfully', 'success');
      fetchInstructions();
    } catch (error) {
      console.error('Error creating general instruction:', error);
      showSnackbar('Failed to create general instruction', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInstruction = async () => {
    try {
      setLoading(true);
      
      if (!formData.title || !formData.description) {
        showSnackbar('Title and description are required', 'error');
        return;
      }
      
      await api.put(`/general-instructions/${selectedInstruction.id}`, formData);
      
      setOpenEditDialog(false);
      showSnackbar('General instruction updated successfully', 'success');
      fetchInstructions();
    } catch (error) {
      console.error('Error updating general instruction:', error);
      showSnackbar('Failed to update general instruction', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInstruction = async () => {
    try {
      setLoading(true);
      
      await api.delete(`/general-instructions/${selectedInstruction.id}`);
      
      setOpenDeleteDialog(false);
      showSnackbar('General instruction deleted successfully', 'success');
      fetchInstructions();
    } catch (error) {
      console.error('Error deleting general instruction:', error);
      showSnackbar('Failed to delete general instruction', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading && instructions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are viewing general instructions in read-only mode. Admin access is required to create, edit, or delete instructions.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">General Instructions Manager</Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={currentLanguage}
              onChange={(e) => setCurrentLanguage(e.target.value)}
              label="Language"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang.charAt(0).toUpperCase() + lang.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          {isReorderMode ? (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={saveReordering}
                sx={{ mr: 2 }}
              >
                Save Order
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleReorderToggle}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="contained" 
                startIcon={<Add />} 
                onClick={handleOpenAddDialog}
                sx={{ mr: 2 }}
                disabled={!isAdmin}
              >
                Add New
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleReorderToggle}
                disabled={!isAdmin}
              >
                Reorder
              </Button>
            </>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {isReorderMode && <TableCell width="5%"></TableCell>}
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(isReorderMode ? tempInstructions : instructions).map((instruction, index) => (
              isReorderMode ? (
                <DraggableRow 
                  key={instruction.id} 
                  id={instruction.id} 
                  index={index}
                  moveRow={moveRow}
                >
                  <TableCell>
                    <DragHandle />
                  </TableCell>
                  <TableCell>{instruction.languageSpec?.[currentLanguage]?.name || instruction.title}</TableCell>
                  <TableCell>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: instruction.languageSpec?.[currentLanguage]?.description || instruction.description || '' 
                      }} 
                    />
                  </TableCell>
                  <TableCell>{instruction.isActive ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>{new Date(instruction.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton disabled>
                      <Edit />
                    </IconButton>
                  </TableCell>
                </DraggableRow>
              ) : (
                <TableRow key={instruction.id}>
                  <TableCell>{instruction.languageSpec?.[currentLanguage]?.name || instruction.title}</TableCell>
                  <TableCell>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: instruction.languageSpec?.[currentLanguage]?.description || instruction.description || '' 
                      }} 
                    />
                  </TableCell>
                  <TableCell>{instruction.isActive ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>{new Date(instruction.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenEditDialog(instruction)} disabled={!isAdmin}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleOpenDeleteDialog(instruction)} disabled={!isAdmin}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New General Instruction</DialogTitle>
        <DialogContent>
          <TextField
            name="title"
            label="Title"
            fullWidth
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <Box mt={2} mb={2}>
            <Typography variant="subtitle1" gutterBottom>Description</Typography>
            <EnhancedRichTextEditor
              content={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Enter instruction description..."
            />
          </Box>
          <FormControlLabel
            control={
              <Switch 
                name="isActive" 
                checked={formData.isActive} 
                onChange={handleInputChange} 
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddInstruction} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit General Instruction</DialogTitle>
        <DialogContent>
          <TextField
            name="title"
            label="Title"
            fullWidth
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <Box mt={2} mb={2}>
            <Typography variant="subtitle1" gutterBottom>Description</Typography>
            <EnhancedRichTextEditor
              content={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Enter instruction description..."
            />
          </Box>
          <FormControlLabel
            control={
              <Switch 
                name="isActive" 
                checked={formData.isActive} 
                onChange={handleInputChange} 
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditInstruction} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete General Instruction</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedInstruction?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteInstruction} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Wrap the component with DndProvider for drag and drop functionality
const WrappedGeneralInstructionsManager = () => (
  <DndProvider backend={HTML5Backend}>
    <GeneralInstructionsManager />
  </DndProvider>
);

export default WrappedGeneralInstructionsManager;